import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudEmptyState, CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useWorkspaceLocationId } from "@/lib/location-context";
import { Loader2, Plus, Check, X as XIcon } from "lucide-react";

interface Location { id: string; name: string; active: boolean }
interface ProductOpt { id: string; name: string }
interface IngredientOpt { id: string; nombre: string; unidadBase: string }

interface CountSummary {
  id: string;
  locationId: string;
  status: "DRAFT" | "COMPLETADO" | "CANCELADO";
  createdAt: string;
}
interface CountLine {
  id: string;
  productId: string | null;
  ingredientId: string | null;
  itemName?: string;
  qtySystem: number;
  qtyCounted: number | null;
}
interface CountDetail {
  count: CountSummary;
  lines: CountLine[];
}

const STATUS_LABEL: Record<CountSummary["status"], string> = {
  DRAFT: "En curso",
  COMPLETADO: "Completado",
  CANCELADO: "Cancelado",
};
const STATUS_TONE: Record<CountSummary["status"], "secondary" | "default" | "destructive"> = {
  DRAFT: "secondary",
  COMPLETADO: "default",
  CANCELADO: "destructive",
};

function locationName(locs: Location[], id: string): string {
  return locs.find((l) => l.id === id)?.name ?? id;
}

export const Route = createFileRoute("/app/conteo-fisico")({
  component: ConteoFisicoPage,
  head: () => ({ meta: [{ title: "Conteo físico — Structura Market" }] }),
});

function ConteoFisicoPage() {
  const tid = useWorkspaceTenantId();
  const [locations, setLocations] = useState<Location[]>([]);
  const [products, setProducts] = useState<ProductOpt[]>([]);
  const [ingredients, setIngredients] = useState<IngredientOpt[]>([]);
  const [counts, setCounts] = useState<CountSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CountDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [countedInputs, setCountedInputs] = useState<Record<string, string>>({});

  const load = () => {
    if (!tid) return;
    setLoading(true);
    Promise.all([
      apiGetJson<Location[]>("/api/locations", tid),
      apiGetJson<{ items: ProductOpt[] } | ProductOpt[]>("/api/products", tid),
      apiGetJson<{ ingredients?: IngredientOpt[] }>("/api/ingredients", tid).catch(() => ({ ingredients: [] })),
      apiGetJson<{ counts: CountSummary[] }>("/api/stock-counts", tid),
    ])
      .then(([locsRes, prodRes, ingRes, countsRes]) => {
        setLocations(locsRes.filter((l) => l.active));
        setProducts(Array.isArray(prodRes) ? prodRes : prodRes.items);
        setIngredients(ingRes.ingredients ?? []);
        setCounts(countsRes.counts);
      })
      .catch(() => toast.error("Error al cargar conteos"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [tid]);

  const openDetail = (id: string) => {
    if (!tid) return;
    setSelectedId(id);
    setDetailLoading(true);
    apiGetJson<CountDetail>(`/api/stock-counts?id=${id}`, tid)
      .then((d) => {
        setDetail(d);
        const inputs: Record<string, string> = {};
        for (const line of d.lines) inputs[line.id] = line.qtyCounted != null ? String(line.qtyCounted) : "";
        setCountedInputs(inputs);
      })
      .catch(() => toast.error("Error al cargar el conteo"))
      .finally(() => setDetailLoading(false));
  };

  const saveLine = async (lineId: string) => {
    if (!tid || !selectedId) return;
    const qty = Number(countedInputs[lineId]);
    if (!Number.isFinite(qty) || qty < 0) return toast.error("Cantidad inválida");
    try {
      await apiSendJson(`/api/stock-counts?id=${selectedId}`, "PATCH", { action: "setLine", lineId, qtyCounted: qty }, tid);
      openDetail(selectedId);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo guardar");
    }
  };

  const runAction = async (action: "complete" | "cancel") => {
    if (!tid || !selectedId) return;
    setSaving(true);
    try {
      const res = await apiSendJson<{ adjustedLines?: number }>(`/api/stock-counts?id=${selectedId}`, "PATCH", { action }, tid);
      toast.success(action === "complete" ? `Conteo confirmado — ${res.adjustedLines ?? 0} ajustes aplicados` : "Conteo cancelado");
      load();
      openDetail(selectedId);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo actualizar el conteo");
    } finally {
      setSaving(false);
    }
  };

  const pendingCount = detail?.lines.filter((l) => l.qtyCounted == null).length ?? 0;

  return (
    <ClinicalPageShell className="mx-auto w-full max-w-[1400px] space-y-4">
      <CrudShellHeader
        title="Conteo físico"
        subtitle="Contá stock por sucursal y ajustá diferencias con trazabilidad"
        actions={
          <Button onClick={() => setCreateOpen(true)} disabled={locations.length < 1} className="gap-2">
            <Plus className="h-4 w-4" /> Nuevo conteo
          </Button>
        }
      />

      {loading ? (
        <Loading />
      ) : counts.length === 0 ? (
        <CrudEmptyState title="Todavía no hay conteos" description="Creá el primero para verificar el stock real de una sucursal." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-4">
          <div className="space-y-2">
            {counts.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => openDetail(c.id)}
                className={`w-full text-left rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                  selectedId === c.id ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{locationName(locations, c.locationId)}</span>
                  <Badge variant={STATUS_TONE[c.status]}>{STATUS_LABEL[c.status]}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(c.createdAt).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}
                </p>
              </button>
            ))}
          </div>

          <div className="rounded-lg border p-4">
            {!detail ? (
              <p className="text-sm text-muted-foreground">Elegí un conteo de la lista.</p>
            ) : detailLoading ? (
              <Loading />
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="font-semibold">{locationName(locations, detail.count.locationId)}</p>
                  <Badge variant={STATUS_TONE[detail.count.status]}>{STATUS_LABEL[detail.count.status]}</Badge>
                </div>

                <div className="space-y-2">
                  {detail.lines.map((line) => {
                    const counted = countedInputs[line.id] ?? "";
                    const diff = counted !== "" ? Number(counted) - line.qtySystem : null;
                    return (
                      <div key={line.id} className="flex items-center justify-between gap-2 text-sm border-b pb-2 last:border-0">
                        <span className="min-w-0 truncate">{line.itemName ?? line.productId ?? line.ingredientId}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-muted-foreground tabular-nums">Sistema: {line.qtySystem}</span>
                          {detail.count.status === "DRAFT" ? (
                            <>
                              <Input
                                type="number"
                                min={0}
                                step="0.001"
                                className="h-7 w-20"
                                value={counted}
                                onChange={(e) => setCountedInputs((prev) => ({ ...prev, [line.id]: e.target.value }))}
                              />
                              {diff != null && diff !== 0 && (
                                <span className={`text-xs tabular-nums ${diff > 0 ? "text-green-500" : "text-red-500"}`}>
                                  {diff > 0 ? "+" : ""}{diff}
                                </span>
                              )}
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => saveLine(line.id)}>
                                <Check className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs tabular-nums font-medium">
                              Contado: {line.qtyCounted ?? "—"}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {detail.count.status === "DRAFT" && (
                  <div className="flex items-center gap-2 pt-2">
                    <Button size="sm" disabled={saving} onClick={() => runAction("complete")} className="gap-2">
                      {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                      Confirmar conteo
                    </Button>
                    <Button size="sm" variant="destructive" disabled={saving} onClick={() => runAction("cancel")} className="gap-2">
                      <XIcon className="h-4 w-4" /> Cancelar
                    </Button>
                    {pendingCount > 0 && (
                      <span className="text-xs text-muted-foreground">{pendingCount} ítem(s) sin contar — se ignoran al confirmar</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <CreateCountDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        locations={locations}
        products={products}
        ingredients={ingredients}
        tid={tid}
        onCreated={() => { setCreateOpen(false); load(); }}
      />
    </ClinicalPageShell>
  );
}

function CreateCountDialog({
  open, onOpenChange, locations, products, ingredients, tid, onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  locations: Location[];
  products: ProductOpt[];
  ingredients: IngredientOpt[];
  tid: string | null;
  onCreated: () => void;
}) {
  const contextLocationId = useWorkspaceLocationId();
  const [locationId, setLocationId] = useState("");
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [selectedIngredientIds, setSelectedIngredientIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // SCRUM-72: default desde el contexto único de sucursal activa.
      setLocationId(contextLocationId ?? locations[0]?.id ?? "");
      setSelectedProductIds([]);
      setSelectedIngredientIds([]);
    }
  }, [open, locations, contextLocationId]);

  const toggleProduct = (id: string) =>
    setSelectedProductIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleIngredient = (id: string) =>
    setSelectedIngredientIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async () => {
    if (!tid) return;
    if (!locationId) return toast.error("Elegí una sucursal");
    if (!selectedProductIds.length && !selectedIngredientIds.length) return toast.error("Elegí al menos un ítem");
    setSaving(true);
    try {
      await apiSendJson("/api/stock-counts", "POST", {
        locationId,
        productIds: selectedProductIds,
        ingredientIds: selectedIngredientIds,
      }, tid);
      toast.success("Conteo iniciado");
      onCreated();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo iniciar el conteo");
    } finally {
      setSaving(false);
    }
  };

  const items = useMemo(
    () => [
      ...products.map((p) => ({ id: p.id, name: p.name, kind: "product" as const })),
      ...ingredients.map((i) => ({ id: i.id, name: `${i.nombre} (insumo)`, kind: "ingredient" as const })),
    ],
    [products, ingredients],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Nuevo conteo físico</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Sucursal</Label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Ítems a contar</Label>
            <div className="mt-1 max-h-64 overflow-y-auto rounded-md border p-2 space-y-1">
              {items.map((item) => {
                const checked = item.kind === "product" ? selectedProductIds.includes(item.id) : selectedIngredientIds.includes(item.id);
                return (
                  <label key={`${item.kind}-${item.id}`} className="flex items-center gap-2 text-sm px-1 py-1 rounded hover:bg-muted/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => (item.kind === "product" ? toggleProduct(item.id) : toggleIngredient(item.id))}
                    />
                    {item.name}
                  </label>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {selectedProductIds.length + selectedIngredientIds.length} seleccionado(s)
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Iniciar conteo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
